import { memo, useRef } from 'react';
import FloatAnimation, { FloatAnimationVariant } from './FloatAnimation';

interface IconProps {
  className?: string;
}

// Cloud paths used for both clip-path and visual rendering
const CLOUD_2D_PATH =
  'M55 51C63.2843 51 70 57.7157 70 66C70 70.7725 67.7704 75.0233 64.2969 77.7705C66.5679 79.7856 68 82.7251 68 86C68 92.0751 63.0751 97 57 97C52.7189 97 49.0121 94.5528 47.1943 90.9824C44.4473 94.0608 40.4505 96 36 96C27.7157 96 21 89.2843 21 81C21 72.7157 27.7157 66 36 66C37.3892 66 38.734 66.1896 40.0107 66.543C40.0043 66.3628 40 66.1818 40 66C40 57.7157 46.7157 51 55 51Z';

const CLOUD_3D_PATH =
  'M73 80C73.0001 73.9249 77.9249 69 84 69C90.0751 69 94.9999 73.9249 95 80C95 86.0751 90.0751 91 84 91C77.9249 91 73 86.0751 73 80Z';

function HazeIcon({ className }: IconProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  return (
    <svg
      ref={svgRef}
      width="120"
      height="120"
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-test="weather-icon-haze"
    >
      {/* Main cloud - no blur, just animation */}
      <path
        d="M60.1152 23C72.3292 23 82.2305 32.9012 82.2305 45.1152C82.2305 45.5608 82.2164 46.0033 82.1904 46.4424H84.3457C93.6283 46.4424 101.154 53.9674 101.154 63.25C101.154 72.5325 93.6282 80.0576 84.3457 80.0576H34.8076C25.5251 80.0576 18.0002 72.5325 18 63.25C18 53.9674 25.525 46.4424 34.8076 46.4424H38.04C38.014 46.0033 38 45.5608 38 45.1152C38 32.9013 47.9013 23.0001 60.1152 23Z"
        fill="white"
        fillOpacity="0.7"
      >
        <FloatAnimation />
      </path>
      {/* 2D cloud group - foreignObject and path animate together */}
      <g>
        <FloatAnimation variant={FloatAnimationVariant.Downwash} />
        {/* Backdrop blur effect using foreignObject with clip-path */}
        <foreignObject x="19" y="49" width="53" height="50">
          <div
            // @ts-expect-error xmlns is valid for foreignObject content
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              clipPath: 'url(#bgblur_haze_2d_clip)',
              height: '100%',
              width: '100%',
            }}
          />
        </foreignObject>
        {/* 2D cloud overlay with semi-transparent fill */}
        <path d={CLOUD_2D_PATH} fill="white" fillOpacity="0.7" />
      </g>
      {/* 3D cloud group - foreignObject and path animate together */}
      <g>
        <FloatAnimation variant={FloatAnimationVariant.Bounce} />
        {/* Backdrop blur effect using foreignObject with clip-path */}
        <foreignObject x="71" y="67" width="26" height="26">
          <div
            // @ts-expect-error xmlns is valid for foreignObject content
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              clipPath: 'url(#bgblur_haze_3d_clip)',
              height: '100%',
              width: '100%',
            }}
          />
        </foreignObject>
        {/* 3D cloud overlay with semi-transparent fill */}
        <path d={CLOUD_3D_PATH} fill="white" fillOpacity="0.7" />
      </g>
      <defs>
        {/* Clip path for 2D cloud backdrop blur - offset to match foreignObject position */}
        <clipPath id="bgblur_haze_2d_clip" transform="translate(-19 -49)">
          <path d={CLOUD_2D_PATH} />
        </clipPath>
        {/* Clip path for 3D cloud backdrop blur - offset to match foreignObject position */}
        <clipPath id="bgblur_haze_3d_clip" transform="translate(-71 -67)">
          <path d={CLOUD_3D_PATH} />
        </clipPath>
      </defs>
    </svg>
  );
}

export default memo(HazeIcon);
